import { ChartTypesMap } from "../analyticsTypes";
import useGetCoinsVisitedConfig from "./useGetCoinsVisitedConfig";
import useGetDepositVolumeConfig from "./useGetDepositVolumeConfig";
import useGetGrowthHistoryConfig from "./useGetGrowthHistoryConfig";
import useGetReferralConversionsConfig from "./useGetReferralConversionsConfig";
import useGetReferralFessConfig from "./useGetReferralFessConfig";
import useGetReferralHoldingsConfig from "./useGetReferralHoldingsConfig";
import useGetTradingHistoryConfig from "./useGetTradingHistoryConfig";
import { useGetTimeToFirstTradeConfig } from "./useGetTimeToFirstTrade";
import useGetAssetsTradedConfig from "./useGetAssetsTradedConfig";

/**
 * Custom hook that aggregates all chart configurations into a single map.
 *
 * This hook combines configurations from multiple chart-specific hooks into a unified
 * configuration object that adheres to the ChartTypesMap interface. Each configuration
 * is fetched using its own specialized hook and merged into the final result.
 *
 * The hook manages configurations for:
 * - Referral fees
 * - Referral holdings
 * - Trading history
 * - Growth history
 * - Visited coins
 * - Referral conversions
 * - Deposit volume
 *
 * @returns {ChartTypesMap} A complete mapping of chart names to their configurations
 *
 * @example
 * const chartConfigs = useGetChartConfig();
 * const funnelChartConfig = chartConfigs.referralConversions;
 */
const useGetChartConfig = (): ChartTypesMap => {
  const referralFeesConfig = useGetReferralFessConfig();
  const referralHoldingsConfig = useGetReferralHoldingsConfig();
  const tradingHistoryConfig = useGetTradingHistoryConfig();
  const growthHistoryConfig = useGetGrowthHistoryConfig();
  const coinsVisitedConfig = useGetCoinsVisitedConfig();
  const referralConversionsConfig = useGetReferralConversionsConfig();
  const depositVolumeConfig = useGetDepositVolumeConfig();
  const timeToFirstTradeConfig = useGetTimeToFirstTradeConfig();
  const assetsTradedConfig = useGetAssetsTradedConfig();

  return {
    ...referralFeesConfig,
    ...referralHoldingsConfig,
    ...tradingHistoryConfig,
    ...growthHistoryConfig,
    ...coinsVisitedConfig,
    ...referralConversionsConfig,
    ...depositVolumeConfig,
    ...timeToFirstTradeConfig,
    ...assetsTradedConfig,
  };
};

export default useGetChartConfig;
